import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, ArrowRight, X } from "lucide-react";

const STORAGE_KEY_CONTRACTS = "doserne_contracts";
const DISMISS_KEY = "expiring_contracts_dismissed";
const WARN_DAYS = 14;

/**
 * Reads contracts from localStorage and surfaces those expiring within 14 days.
 * If no contract is expiring → component returns null.
 * Used on dashboards (admin + holding).
 */
export default function ExpiringContractsWidget({ compact = false }) {
  const [expiring, setExpiring] = useState([]);
  const [expired, setExpired] = useState([]);
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_CONTRACTS);
      const list = saved ? JSON.parse(saved) : [];
      const dismissedMap = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
      setDismissed(dismissedMap);

      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const exp = [];
      const passed = [];

      list.forEach((c) => {
        if (!c.endDate || c.contractType === "Permanent") return;
        const end = new Date(c.endDate);
        end.setHours(0, 0, 0, 0);
        const daysLeft = Math.floor((end - now) / (1000 * 60 * 60 * 24));
        const enriched = { ...c, daysLeft };

        if (daysLeft < 0) {
          passed.push(enriched);
        } else if (daysLeft <= WARN_DAYS) {
          exp.push(enriched);
        }
      });

      // Sort by least days remaining first
      exp.sort((a, b) => a.daysLeft - b.daysLeft);
      passed.sort((a, b) => a.daysLeft - b.daysLeft);
      setExpiring(exp);
      setExpired(passed);
    } catch (e) {
      console.warn("Could not read contracts from localStorage", e);
    }
  }, []);

  const handleDismiss = (contractId) => {
    const next = { ...dismissed, [contractId]: new Date().toISOString() };
    setDismissed(next);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch (e) {
      // localStorage might be unavailable in private mode
    }
  };

  // Filter out dismissed (dismissed within 7 days)
  const isDismissed = (cId) => {
    const dt = dismissed[cId];
    if (!dt) return false;
    const since = (Date.now() - new Date(dt).getTime()) / (1000 * 60 * 60 * 24);
    return since < 7;
  };

  const activeExpiring = expiring.filter((c) => !isDismissed(c.id));
  const activeExpired = expired.filter((c) => !isDismissed(c.id));

  if (activeExpiring.length === 0 && activeExpired.length === 0) return null;

  return (
    <Card
      className="bg-gradient-to-br from-[#F7931A]/10 to-red-500/5 border-[#F7931A]/40"
      data-testid="expiring-contracts-widget"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#F7931A]" />
            <h3 className="text-white font-bold">
              Kontrakter som krever oppmerksomhet
            </h3>
            <Badge className="bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/40">
              {activeExpiring.length + activeExpired.length}
            </Badge>
          </div>
          <Link to="/holding/contract-generator">
            <Button variant="ghost" size="sm" className="text-[#F7931A] hover:bg-[#F7931A]/10">
              Åpne <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="space-y-2">
          {/* Already expired */}
          {activeExpired.slice(0, compact ? 2 : 5).map((c) => (
            <ContractRow key={c.id} c={c} onDismiss={handleDismiss} expired />
          ))}
          {/* Expiring soon */}
          {activeExpiring.slice(0, compact ? 3 : 8).map((c) => (
            <ContractRow key={c.id} c={c} onDismiss={handleDismiss} />
          ))}
        </div>

        <p className="text-[#94A3B8] text-xs mt-4 italic">
          ⚠️ Hvis ingen ny kontrakt sendes innen utløpsdatoen, regnes kontrakten som <strong>ikke fornyet</strong>.
        </p>
      </CardContent>
    </Card>
  );
}

const ContractRow = ({ c, onDismiss, expired = false }) => {
  const colorClass = expired
    ? "bg-red-500/10 border-red-500/30 text-red-300"
    : c.daysLeft <= 3
    ? "bg-red-500/10 border-red-500/30 text-red-200"
    : c.daysLeft <= 7
    ? "bg-orange-500/10 border-orange-500/30 text-orange-200"
    : "bg-yellow-500/10 border-yellow-500/30 text-yellow-200";

  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${colorClass}`}
      data-testid={`expiring-row-${c.id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Clock className="w-4 h-4 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-white font-medium text-sm truncate">
            {c.empName || "—"}{" "}
            <span className="text-white/50 font-normal text-xs">
              ({c.positionLabel || c.position})
            </span>
          </div>
          <div className="text-xs opacity-80">
            {expired ? (
              <>Utløpt {Math.abs(c.daysLeft)} dager siden ({c.endDate})</>
            ) : c.daysLeft === 0 ? (
              <>Utløper i dag ({c.endDate})</>
            ) : (
              <>Utløper om {c.daysLeft} dager ({c.endDate})</>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={() => onDismiss(c.id)}
        className="text-white/40 hover:text-white p-1 flex-shrink-0"
        title="Skjul i 7 dager"
        data-testid={`dismiss-${c.id}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
